<template>
  <div role="group">
     <p
      aria-live="polite"
      aria-atomic="true"
      class="aria-feedback">
      {{ interactionFeedbackAria }}
    </p>
    <div
      v-for="(io, idx) in interaction.interactionOptions"
      :key="idx"
      class="lighten-5 checkbox-option"
      :class="[
      (
        interactionMeta.opacity === 1
        && !interactionMeta.darkMode
      )
        ? feedbackType(io)
        : 'transparent-mode-' + feedbackType(io),
      ]"
      @keyup.enter="handleEnter(io)">
      <v-checkbox
        @change="updateCheckboxAnswer()"
        v-model="interactionAttempt.interactionOptionIds"
        :value="io.id"
        :disabled="(
          interactionMeta.isInteractionAnswered
          || interactionMeta.isProcessingData
          || (
            isLiveSession
            && interactionMeta.pastSessionTime
          )
        )"
        :aria-label="io.option.replace(/(\<.*?\>)/g, '')"
        :dark="interactionMeta.darkMode"
        hide-details>
        <div
          :id="'io'+io.id+'label'"
          class="pp-interaction-body full-width"
          slot="label"
          v-html="io.option">
        </div>
      </v-checkbox>
      <p
        class="broadcast__counter-tag primary--text"
        v-if="broadcastTeacher">
        ({{ optionCount && optionCount[io.id] || 0 }})
      </p>
      <interaction-attachments :attachments="io.interactionOptionAttachments" />
      <blockquote
        v-show="markAnswer(io)"
        :key="interactionCheckboxFeedbackKey"
        class="feedback-blockquote clearfix pp-interaction-feedback position-fix">
        <p
          :class="{
            'mb-0': (
              !io.feedback
              || interaction.interaction_type === 'poll_q'
            ), 'white--text': interactionMeta.darkMode }">
          {{formatFeedback(feedbackType(io))}}
        </p>
        <p
          v-show="showFeedback(io)"
          :class="{ 'white--text': interactionMeta.darkMode }"
          v-html="io.feedback"
          tabindex="-1">
        </p>
        <interaction-attachments :attachments="io.interactionOptionFeedbackAttachments" />
      </blockquote>
    </div>
  </div>
</template>

<script>

/* global _ */

// Components:
import InteractionAttachments from '@/components/interaction/InteractionAttachments';
import EventBus from '@/buses/EventBus';

// Library / Plugins:
import {
  mapGetters,
  mapState
} from 'vuex';

export default {
  name: 'question-checkbox',
  props: [
    'interactionId',
    'visible',
    'optionCount',
  ],
  components: {
    InteractionAttachments,
  },
  data() {
    return {
      answerData: {},
      correctAnswers: [],
      interactionFeedbackAria: '',
      interactionCheckboxFeedbackKey: 1,
    }
  },
  computed: {
    ...mapState('player', [
      'isLiveSession',
    ]),
    ...mapGetters('player', [
      'user',
      'broadcastTeacher',
    ]),
    interaction() {
      return this.$store.getters['interactions/interaction'](this.interactionId) || {};
    },
    interactionAttempt() {
      return this.$store.getters['interactions/interactionAttempt'](this.interactionId) || {};
    },
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
    correctOptionIds() {
      return _.map(this.interactionMeta.correctAnswers, 'id');
    },
  },
  methods: {
    constructAriaFeedback() {
      let ariaFeedback = '';
      for (const io of this.interaction.interactionOptions) {
        const selectionFeedback = `Selection ${io.option.replace(/(\<.*?\>)/g, '')} was ${this.formatFeedback(this.feedbackType(io))}. ${this.showFeedback(io) ? 'Feedback provided: ' + io.feedback.replace(/(\<.*?\>)/g, '') : ''} `;
        ariaFeedback += selectionFeedback;
      }
      setTimeout(() => {this.interactionFeedbackAria = ariaFeedback;}, 100); // JAWS will not read aria-live region without this timeout
    },
    feedbackType(io) {
      if (this.markAnswer(io)) {
        // interaction answered
        // if hide_correct_answer_always - dont show correct/incorrect/missed
        if (
          (
            _.get(this, 'interaction.config.hide_correct_answer_always', false)
            && !_.get(this, 'interaction.config.only_show_correct_answer_on_last_attempt', false)
          )
          || (
            this.interactionMeta.attempts < this.interaction.attempts
            && _.get(this.interaction, 'config.show_highlighting_last_attempt_only', false)
            && !this.interactionMeta.isAnswerCorrect
          )
        ) return '';
        if (this.interactionAttempt.interactionOptionIds.includes(io.id)) {
          // option selected
          if (this.interaction.interactionType === 'poll_q') return 'selected';
          else return this.correctOptionIds.includes(io.id) ?  'correct' : 'incorrect';
        }
        // option not selected
        return this.correctOptionIds.includes(io.id) ? 'missed' : '';
      }
      return '';
    },
    markAnswer(io) {
      // interaction answered and option has feedback
      if (
        this.interactionMeta.isInteractionAnswered
        && (
          (
            this.interaction.attempts > 0
            && this.interactionMeta.attempts >= this.interaction.attempts
          )
          || this.interactionMeta.isAnswerCorrect
          || !_.get(this.interaction, 'config.show_feedback_last_attempt_only', 1)
          || (
            this.interactionMeta.attempts === this.interaction.attempts
            && _.get(this.interaction, 'config.only_show_correct_answer_on_last_attempt', 0)
          )
        )
      ) {
        if (this.interaction.interactionType === 'check_all_apply') {
          // return true for selected and correct options
          if (
            this.interaction.attempts > 0
            && this.interactionMeta.attempts < this.interaction.attempts
            && _.get(this.interaction, 'config.only_show_correct_answer_on_last_attempt', 0)
          ) {
            return this.interactionAttempt.interactionOptionIds.includes(io.id);  
          }
          return this.interactionAttempt.interactionOptionIds.includes(io.id) || this.correctOptionIds.includes(io.id);
        } else if (this.interaction.interactionType === 'poll_q') {
          // only show options the user selected
          return this.interactionAttempt.interactionOptionIds.includes(io.id)
        }
      }
      return false;
    },
    showFeedback(io) {
      return this.markAnswer(io) && (this.interaction.config.show_unselected_correct_feedback || this.interactionAttempt.interactionOptionIds.includes(io.id)) && io.feedback; // this.interaction.config.show_unselected_correct_feedback this.interactionAttempt.interactionOptionIds.includes(io.id)
    },
    formatFeedback(text) {
      if (text === 'selected') return '';
      return _.startCase(_.toLower(text));
    },
    updateCheckboxAnswer(interactionOption) {
      this.interactionMeta.isAnswerEmpty = (this.interactionAttempt.interactionOptionIds.length === 0);
      if (interactionOption) { // If passed an interaction option, user selected option with the enter key
        this.updateInteractionAttempt(interactionOption, true);
      } else { // user selected interaction option with click
        this.interactionAttempt.responseText = [];
        this.interactionAttempt.interactionOptionIds.forEach(ioi => {
          let io = this.interaction.interactionOptions.find(ioo => ioo.id === ioi);
          this.interactionAttempt.responseText.push(io.option);
        });
      }
    },
    handleEnter(io) {
      this.updateInteractionAttempt(io, false);
      this.updateCheckboxAnswer(io);
    },
    /**
     * Update the interaction attempt optionIds or the responseText array.
     * 
     * @param interactionOption interaction option the user selected.
     * @param updateResponseText boolean that indicates to update the responseText array. False by default.
     */
    updateInteractionAttempt(interactionOption, updateResponseText = false) {
      const arrayToUpdate = this.interactionAttempt[(updateResponseText) ? 'responseText' : 'interactionOptionIds'];
      const value = (updateResponseText) ? interactionOption.option : interactionOption.id;
      if (!arrayToUpdate.includes(value)) {
        arrayToUpdate.push(value);
      } else {
        arrayToUpdate.splice(arrayToUpdate.indexOf(value), 1);
      }
    }
  },
  watch: {
    'interactionMeta.isInteractionAnswered'() {
      this.constructAriaFeedback();
    },
    'visible'(newValue, oldValue) {
      if (!newValue && oldValue) this.interactionCheckboxFeedbackKey += 1;
    },
  },
  mounted () {
    EventBus.$on('retakeInteraction', interactionId => {
      if (interactionId === this.interactionId) {
        this.interactionCheckboxFeedbackKey += 1;
      }
    });
  },
};
</script>

<style>
/*remove margin from p tags if they are the last one of the radio*/
  form p:last-child {
    margin-bottom: 0;
  }
  .v-input--checkbox .v-input--selection-controls__input {
    align-self: flex-start;
  }
  .checkbox-option {
    position: relative;
  }
  .broadcast__counter-tag {
    padding: 5px 10px;
    line-height: 0.9em;
    font-size: 0.9em;
    position: absolute;
    top: 0;
    right: 0;
  }
  .checkbox-option label {
    width: 83%;
  }
  /* Check all answers with audio need a min-width to display properly.  Otherwise they inherit the width of the text of an answer choice. */
  audio {
    min-width: 220px;
  }

  .aria-feedback {
    text-indent: -10000px;
    font-size: 0.1px;
  }
</style>
